import { gql } from '@soundxyz/gql-string';
import { getChainName } from '@soundxyz/utils/const';
import { parseReferralSource, stringifyReferralSource } from '@soundxyz/utils/constants/embeds';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { graphqlClientHeaders } from '@context/headers';
import { type MintReferral, MintReferralType } from '@context/mintReferral';
import { ParamsStore } from '@context/params';
import { fetchGQL, SetAffiliatePurchaseSourceDocument } from '@graphql/reactQuery';
import { logError } from '@hooks/useLogErrorNew';
import { logInfo } from '@hooks/useLogInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation setAffiliatePurchaseSource($input: AffiliateSourceInput!) {
    setAffiliatePurchaseSource(input: $input) {
      __typename
      ... on Error {
        message
      }
    }
  }
`);

const pillar = PILLARS.MINTING;
const isArtistOnlyPage = false;

export function sendReferralSourceIfPresent({
  txHash,
  chainId,
  referral,
}: {
  txHash: string;
  chainId: number;
  referral: MintReferral;
}) {
  let referralSource = ParamsStore.referral_source;

  if (referral) {
    const { referralType, referralData } = referral;
    if (!referralSource && referralType) {
      if (referralType === MintReferralType.Playlist) {
        referralSource = stringifyReferralSource({
          playlistId: referralData.playlistId,
        });
      } else if (referralType === MintReferralType.Post) {
        referralSource = stringifyReferralSource({
          postId: referralData.postId,
        });
      } else {
        referralSource = null;
      }
    }
  }

  const referralSourceValue =
    referralSource && parseReferralSource(referralSource) ? referralSource : false;

  if (!referralSourceValue || !graphqlClientHeaders['auth-token']) return;

  const errorMessage = `Unexpected error while trying to set referral_source of ${referralSourceValue}`;
  const logErrorParams = {
    error: new Error(errorMessage),
    message: errorMessage,
    pillar,
    errorType: ERROR_TYPE.UNKNOWN,
    isArtistOnlyPage,
    action: MINTING_ERROR_ACTIONS.SET_REFERRAL_SOURCE_ERROR,
    indexedTags: {
      txHash,
      chainId,
      referralSourceValue,
    },
  };

  fetchGQL(SetAffiliatePurchaseSourceDocument, {
    variables: {
      input: {
        referralSource: referralSourceValue,
        txHash,
        chain: getChainName(chainId),
      },
    },
  })
    .then(response => {
      if (response.errors) {
        logError({
          ...logErrorParams,
          level: 'warning',
          unindexedExtra: {
            response,
          },
        });
        return;
      }

      // This shouldn't really happen on runtime
      if (!response.data) return;

      if (
        response.data.setAffiliatePurchaseSource.__typename !==
        'MutationSetAffiliatePurchaseSourceSuccess'
      ) {
        logError({
          ...logErrorParams,
          level: 'warning',
          unindexedExtra: {
            response,
          },
        });
        return;
      }

      logInfo({
        pillar,
        isArtistOnlyPage,
        message: 'Successfully set referral source',
        action: MINTING_INFO_ACTIONS.SET_REFERRAL_SOURCE_SUCCESS,
        data: {
          txHash,
          chainId,
          referralSourceValue,
        },
      });
    })
    .catch(err => {
      logError({
        ...logErrorParams,
        level: 'warning',
        error: err,
      });
    });
}
